import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaChevronDown } from "react-icons/fa";
import InfoTip from "../components/InfoTip";
import "../assets/css/EditTenant.css";
import "../assets/css/DigixLeads.css";
import "../assets/css/AddLead.css";
import "../assets/css/Issues.css";
import axiosInstance from "../components/api/axiosInstance";
import { useConfirm } from "../components/ConfirmModal";
import { getAuthItem } from "../utils/authStorage";
import { formatPascal } from "../utils/formatters";
import {
  PORTAL_OWN_DOMAIN,
  PORTAL_SHARED,
  normalizePortalDomain,
  tenantLoginEmailError,
} from "../utils/loginDomain";
import OwnerPasswordField from "../components/OwnerPasswordField";
import TenantSourceField from "../components/TenantSourceField";
import StorageProviderField from "../components/StorageProviderField";
import AddressSearch from "../components/AddressSearch";
import useAddressAutofill from "../utils/useAddressAutofill";
import PortalLoginFields, {
  autoOwnerEmail,
  ownerDomainError,
  portalHostFor,
  ownerEmailHint,
  ownerEmailPlaceholder,
  portalSettingsError,
} from "../components/PortalLoginFields";
import useOwnerLookup, { describeExistingOwner, OWNER_EMAIL_INFO } from "../utils/useOwnerLookup";
import ServicePicker from "../components/ServicePicker";
import TenantBillToSection from "../components/TenantBillToSection";
import BillToSelfSwitch from "../components/BillToSelfSwitch";
import {
  DEFAULT_COUNTRY_CODE,
  normalizeCountryCode,
  splitPhone,
  toLocalPhone,
  formValueWithMobileSanitize,
  mobileValidationMessage,
} from "../utils/phone";

const CUSTOMER_STATUSES = ["Active", "Inactive"];
const CUSTOMER_TYPES = ["Organization", "Individual"];
const BUSINESS_MODELS = [
  { value: "Project", label: "Project Based (e.g. builders, developers)" },
  { value: "Service", label: "Service Based (e.g. stores, food, studios, agencies)" },
];

function EditTenant({ clientId, onBack, onUpdate }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [formData, setFormData] = useState({
    customerType: "Individual",
    firstName: "",
    lastName: "",
    personalEmail: "",
    personalPhone: "",
    altPhone: "",
    aadhar: "",
    taxNumber: "",
    companyName: "",
    domain: "",
    businessModel: "",
    storageProvider: "",
    originalStorageProvider: "",
    companyEmail: "",
    companyPhone: "",
    website: "",
    websiteDomainName: "",
    source: "",
    status: "",
    communicationLanguage: "",
    isQuoteSent: false,
    hasPassword: false,
    portalPassword: "",
    ownerCompanyEmail: "",
    originalOwnerCompanyEmail: "",
    portalType: PORTAL_SHARED,
    portalDomain: "",
    services: [],
    companyAddressLine1: "",
    companyAddressLine2: "",
    companyCity: "",
    companyState: "",
    companyCountry: "",
    companyZipCode: "",
    notes: ""
  });

  const [followupData, setFollowupData] = useState({
    followupDate: "",
    followupType: "",
    nextFollowupDate: "",
    remarks: ""
  });
  const [followupId, setFollowupId] = useState(null);

  const [defaultBillToId, setDefaultBillToId] = useState("");
  // "" = billed to someone else (Default Bill To picker); "Company" / "Individual" = billed directly.
  const [billToAs, setBillToAs] = useState("");
  useEffect(() => {
    if (billToAs === "Company" && !formData.companyName.trim()) setBillToAs("");
  }, [billToAs, formData.companyName]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const ownerEmailUnchanged =
    String(formData.ownerCompanyEmail || "").trim().toLowerCase() ===
    String(formData.originalOwnerCompanyEmail || "").trim().toLowerCase();
  // Changing the owner email to an existing login hands ownership to that person.
  const existingOwner = useOwnerLookup(formData.ownerCompanyEmail, { skip: ownerEmailUnchanged });
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [companyStates, setCompanyStates] = useState([]);
  const [companyCities, setCompanyCities] = useState([]);
  const [selectedCompanyCountryId, setSelectedCompanyCountryId] = useState(null);
  const [selectedCompanyStateId, setSelectedCompanyStateId] = useState(null);
  const addressAutofill = useAddressAutofill({
    countries,
    companyStates,
    companyCities,
    selectedCompanyCountryId,
    selectedCompanyStateId,
    setFormData,
    setSelectedCompanyCountryId,
    setSelectedCompanyStateId,
    setCompanyStates,
    setCompanyCities,
  });
  const [personalPhoneCountryCode, setPersonalPhoneCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [altPhoneCountryCode, setAltPhoneCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [companyPhoneCountryCode, setCompanyPhoneCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [customServices, setCustomServices] = useState({});
  const [editingService, setEditingService] = useState(null);
  const [isAddingServiceToCategory, setIsAddingServiceToCategory] = useState(null);
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [domainSearchQuery, setDomainSearchQuery] = useState("");
  const domainDropdownRef = useRef(null);

  useEffect(() => {
    const fetchServices = async () => {
      setLoadingServices(true);
      try {
        const res = await axiosInstance.get("/service/categories");
        if (res.data && Array.isArray(res.data)) setServiceCategories(res.data);
      } catch (err) {
        console.error("Error fetching services:", err);
      } finally {
        setLoadingServices(false);
      }
    };
    fetchServices();
  }, []);

  // Fetch domains for dropdown (same API as AddTenant)
  useEffect(() => {
    const fetchDomains = async () => {
      setLoadingDomains(true);
      try {
        const res = await axiosInstance.get("/domains");
        const raw = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
            ? res.data.data
            : [];
        const mapped = raw
          .map((d) => ({
            id: d.Domain_Id ?? d.domain_id ?? d.id,
            name: d.Domain_Name ?? d.domain_name ?? d.name ?? "",
            defaultModel: d.Default_Business_Model || ""
          }))
          .filter((d) => d.id != null && d.name.trim() !== "");
        setDomains(mapped);
      } catch (err) {
        console.error("Error fetching domains for customer (edit):", err);
        setDomains([]);
      } finally {
        setLoadingDomains(false);
      }
    };
    fetchDomains();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (domainDropdownRef.current && !domainDropdownRef.current.contains(e.target)) { setDomainDropdownOpen(false); setDomainSearchQuery(""); }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const res = await axiosInstance.get("/countries");
        if (res.data && Array.isArray(res.data)) setCountries(res.data);
      } catch (err) {
        console.error("Error fetching countries:", err);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);


  useEffect(() => {
    if (!selectedCompanyCountryId) {
      setCompanyStates([]);
      return;
    }
    axiosInstance
      .get(`/states/${selectedCompanyCountryId}`)
      .then((res) => {
        if (res.data && Array.isArray(res.data)) setCompanyStates(res.data);
      })
      .catch(() => setCompanyStates([]));
  }, [selectedCompanyCountryId]);

  useEffect(() => {
    if (!selectedCompanyStateId) {
      setCompanyCities([]);
      return;
    }
    axiosInstance
      .get(`/cities/${selectedCompanyStateId}`)
      .then((res) => {
        if (res.data && Array.isArray(res.data)) setCompanyCities(res.data);
      })
      .catch(() => setCompanyCities([]));
  }, [selectedCompanyStateId]);

  useEffect(() => {
    if (!clientId) return;
    const fetchCustomer = async (categories) => {
      setLoading(true);
      try {
        let customerObj = null;
        let addressObj = {};
        let companyAddressObj = {};
        try {
          const res = await axiosInstance.get(`/client/${clientId}`);
          if (res.data) {
            customerObj = res.data.client || res.data;
            addressObj = res.data.address || {};
            companyAddressObj = res.data.company_address || {};
          }
        } catch (e) {
          console.error("Error fetching customer by ID:", e?.response?.data?.message || e?.message || e);
          const allRes = await axiosInstance.get("/clients");
          if (allRes.data && Array.isArray(allRes.data)) {
            const found = allRes.data.find((item) => {
              const c = item.client || item.customer || item;
              return String(c.client_id) === String(clientId);
            });
            if (found) {
              customerObj = found.client || found.customer || found;
              addressObj = found.address || {};
              companyAddressObj = found.company_address || {};
            }
          }
        }

        if (customerObj) {
          // Resolve service names to IDs if serviceCategories are already loaded
          const serviceNames = customerObj.Services || [];
          let resolvedServices = serviceNames;
          
          if (categories && categories.length > 0 && serviceNames.length > 0) {
            const allSubcategories = categories.flatMap(cat =>
              (cat.subcategories || [])
            );
            const resolvedIds = serviceNames
              .map(name => {
                // Try exact match first
                let found = allSubcategories.find(sub => sub.subcategory_name === name);
                // If not found, try case-insensitive match
                if (!found) {
                  found = allSubcategories.find(sub => 
                    sub.subcategory_name.toLowerCase() === name.toLowerCase()
                  );
                }
                return found ? found.subcategory_id : null;
              })
              .filter(Boolean);
            
            if (resolvedIds.length > 0) {
              resolvedServices = resolvedIds;
            }
          }
          
          // Handle Source - API returns Source as string name
          let sourceValue = "";
          if (customerObj.Source) {
            sourceValue = customerObj.Source;
          }
          
          // Prefer explicit country code field; always normalize local digits
          const apiCountryCode = customerObj.Country_Code || customerObj.country_code || "";
          const personalSplit = splitPhone(
            customerObj.Phone || customerObj.phone || "",
            apiCountryCode || DEFAULT_COUNTRY_CODE
          );
          setPersonalPhoneCountryCode(
            normalizeCountryCode(apiCountryCode || personalSplit.countryCode)
          );
          const altSplit = splitPhone(customerObj.Alt_Phone || "", personalSplit.countryCode);
          setAltPhoneCountryCode(altSplit.countryCode || DEFAULT_COUNTRY_CODE);
          const companySplit = splitPhone(customerObj.Company_Phone || "", personalSplit.countryCode);
          setCompanyPhoneCountryCode(companySplit.countryCode || DEFAULT_COUNTRY_CODE);

          // Prefer company_address when it has content; otherwise fall back to address
          const hasCompanyAddr = !!(
            companyAddressObj?.Address_Line_1 ||
            companyAddressObj?.City_Id ||
            companyAddressObj?.Zip_Code
          );
          const addr = hasCompanyAddr ? companyAddressObj : (addressObj || companyAddressObj || {});

          setFormData({
            customerType: customerObj.Customer_Type || customerObj.customer_type || "Individual",
            firstName: customerObj.First_Name || "",
            lastName: customerObj.Last_Name || "",
            personalEmail: customerObj.Email || "",
            // Store only the raw number in state; country code is tracked separately
            personalPhone: personalSplit.number || "",
            altPhone: altSplit.number || "",
            aadhar: customerObj.Aadhar || "",
            taxNumber: customerObj.Tax_Number || "",
            companyName: customerObj.Company_Name || "",
            domain: customerObj.Domain_Id
              ? String(customerObj.Domain_Id)
              : customerObj.Domain_id
                ? String(customerObj.Domain_id)
                : "",
            businessModel: customerObj.Business_Model || "",
            storageProvider: customerObj.Storage_Provider || "",
            originalStorageProvider: customerObj.Storage_Provider || "",
            companyEmail: customerObj.Company_Email || "",
            companyPhone: companySplit.number || "",
            website: customerObj.Website || "",
            websiteDomainName: customerObj.Website_Domain_Name || customerObj.website_domain_name || "",
            source: sourceValue,
            status: customerObj.Status || "",
            communicationLanguage: customerObj.Com_Lang || "",
            isQuoteSent: !!customerObj.Is_Quote_Sent,
            hasPassword: !!customerObj.Has_Password,
            portalPassword: "",
            ownerCompanyEmail: customerObj.Owner_Company_Email || "",
            originalOwnerCompanyEmail: customerObj.Owner_Company_Email || "",
            ownerBusinessCount: Number(customerObj.Owner_Business_Count) || 0,
            wasPrimaryCompany: !!customerObj.Is_Primary_Company,
            isPrimaryCompany: !!customerObj.Is_Primary_Company,
            portalType: customerObj.Portal_Type === PORTAL_OWN_DOMAIN ? PORTAL_OWN_DOMAIN : PORTAL_SHARED,
            portalDomain: customerObj.Portal_Domain || "",
            services: resolvedServices,
            companyAddressLine1: addr.Address_Line_1 || "",
            companyAddressLine2: addr.Address_Line_2 || "",
            companyCity: addr.City_Name || "",
            companyState: addr.State_Name || "",
            companyCountry: addr.Country_Name || "",
            companyZipCode: addr.Zip_Code || "",
            notes: customerObj.Notes || ""
          });
          const ownBillTo = !!customerObj.Is_Bill_To_Company;
          setBillToAs(ownBillTo ? (customerObj.Bill_To_As === "Individual" ? "Individual" : "Company") : "");
          setDefaultBillToId(
            !ownBillTo && customerObj.Default_Bill_To_Id != null ? String(customerObj.Default_Bill_To_Id) : ""
          );
          const coCountry = addr.Country_Id;
          const coState = addr.State_Id;
          if (coCountry) setSelectedCompanyCountryId(coCountry);
          if (coState) setSelectedCompanyStateId(coState);

          try {
            // Use unified followups endpoint with client_id parameter
            const followupRes = await axiosInstance.get(
              `/followups?client_id=${clientId}`
            );
            if (
              followupRes.data &&
              Array.isArray(followupRes.data) &&
              followupRes.data.length > 0
            ) {
              const latest = followupRes.data[0];
              // Format datetime for datetime-local input (YYYY-MM-DDTHH:mm)
              const formatDateTimeLocal = (dateString) => {
                if (!dateString) return "";
                const date = new Date(dateString);
                if (isNaN(date.getTime())) return "";
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                return `${year}-${month}-${day}T${hours}:${minutes}`;
              };
              setFollowupData({
                followupDate: formatDateTimeLocal(latest.Followup_Date),
                followupType: latest.Followup_Type || latest.Type || "",
                nextFollowupDate: formatDateTimeLocal(latest.Next_Followup_Date),
                remarks: latest.Remarks || ""
              });
              // Store followup ID for update
              if (latest.Followup_Id) {
                setFollowupId(latest.Followup_Id);
              }
            }
          } catch (fErr) {
            console.error("Error fetching followups for customer:", fErr?.response?.data?.message || fErr?.message || fErr);
          }
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || "Error fetching customer.";
        console.error("Error fetching customer:", msg);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomer(serviceCategories);
  }, [clientId, serviceCategories]);

  useEffect(() => {
    if (serviceCategories.length === 0 || formData.services.length === 0)
      return;
    const firstService = formData.services[0];
    if (firstService && typeof firstService === "string") {
      const allSub = serviceCategories.flatMap((c) => c.subcategories || []);
      const ids = formData.services
        .map((name) => {
          let found = allSub.find((x) => x.subcategory_name === name);
          if (!found) {
            found = allSub.find((x) => 
              x.subcategory_name.toLowerCase() === name.toLowerCase()
            );
          }
          return found ? found.subcategory_id : null;
        })
        .filter(Boolean);
      
      if (ids.length > 0) {
        const currentIds = formData.services.filter(id => typeof id === 'number');
        const idsMatch = ids.length === currentIds.length && 
          ids.every(id => currentIds.includes(id));
        
        if (!idsMatch) {
          setFormData((prev) => ({ ...prev, services: ids }));
        }
      }
    }
  }, [serviceCategories, formData.services.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewLatestQuote = async () => {
    const win = window.open("", "_blank");
    try {
      const res = await axiosInstance.get(`/client/${clientId}/quote/latest`);
      const url = res.data?.File_Url;
      if (!url) throw new Error("missing url");
      if (win) win.location.href = url;
      else window.open(url, "_blank");
    } catch (err) {
      if (win) win.close();
      const generate = window.confirm(
        "No saved quote was found for this client (it may have been sent before quotes were saved). Generate a new quote?"
      );
      if (generate) {
        navigate("/generate-quote", { state: { customerData: formData, type: "client", clientId } });
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "companyCountry") {
      const c = countries.find((x) => x.country_name === value);
      if (c) setSelectedCompanyCountryId(c.country_id);
      else {
        setSelectedCompanyCountryId(null);
        setSelectedCompanyStateId(null);
        setCompanyStates([]);
        setCompanyCities([]);
        setFormData((prev) => ({ ...prev, companyState: "", companyCity: "" }));
      }
    }
    if (name === "companyState") {
      const s = companyStates.find((x) => x.state_name === value);
      if (s) setSelectedCompanyStateId(s.state_id);
      else {
        setSelectedCompanyStateId(null);
        setCompanyCities([]);
        setFormData((prev) => ({ ...prev, companyCity: "" }));
      }
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : formValueWithMobileSanitize(name, value)
    }));
  };

  const handleSaveService = () => {
    if (!editingService || !editingService.name.trim()) return;
    setCustomServices((prev) => ({
      ...prev,
      [editingService.id]: { name: editingService.name.trim(), categoryId: editingService.categoryId }
    }));
    setFormData((prev) => ({
      ...prev,
      services: [...prev.services, editingService.id]
    }));
    setEditingService(null);
    setIsAddingServiceToCategory(null);
  };

  const handleCancelEditService = () => {
    setEditingService(null);
    setIsAddingServiceToCategory(null);
  };

  const handleDeleteCustomService = async (id) => {
    if (!(await confirm({ title: "Remove Service", message: "Remove this custom service?" }))) return;
    setCustomServices((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setFormData((prev) => ({
      ...prev,
      services: prev.services.filter((s) => s !== id)
    }));
  };

  const handleFollowupChange = (e) => {
    const { name, value } = e.target;
    setFollowupData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.personalEmail) {
      alert("Please fill in required fields (First Name, Last Name, Personal Email, and Phone).");
      return;
    }
    const phoneError = mobileValidationMessage(formData.personalPhone, { required: true, label: "Phone" });
    if (phoneError) {
      alert(phoneError);
      return;
    }
    const altError = mobileValidationMessage(formData.altPhone, { label: "Alternate phone" });
    if (altError) {
      alert(altError);
      return;
    }
    const companyPhoneError = mobileValidationMessage(formData.companyPhone, { label: "Company phone" });
    if (companyPhoneError) {
      alert(companyPhoneError);
      return;
    }
    const ownerCompanyEmail = String(formData.ownerCompanyEmail || "").trim();
    if (!ownerCompanyEmail) {
      alert("Please fill in required fields (Owner Login Email is required).");
      return;
    }
    const portalError = portalSettingsError(formData.portalType, formData.portalDomain);
    if (portalError) {
      alert(portalError);
      return;
    }
    const portalDomain = normalizePortalDomain(formData.portalDomain);
    const ownerDomainMessage = ownerDomainError(ownerCompanyEmail, formData.portalType, portalDomain);
    if (ownerDomainMessage) {
      alert(ownerDomainMessage);
      return;
    }
    const ownerCompanyEmailChanged =
      ownerCompanyEmail.toLowerCase() !== String(formData.originalOwnerCompanyEmail || "").trim().toLowerCase();
    if (ownerCompanyEmailChanged) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerCompanyEmail)) {
        alert("Enter a valid Owner Login Email.");
        return;
      }
      if (!existingOwner) {
        const ownerEmailError = tenantLoginEmailError(ownerCompanyEmail, formData.portalType, portalDomain);
        if (ownerEmailError) {
          alert(ownerEmailError);
          return;
        }
      }
    }
    if (!existingOwner && !formData.hasPassword && !String(formData.portalPassword || "").trim()) {
      alert("Please fill in required fields (Password is required).");
      return;
    }
    if (!formData.services || formData.services.length === 0) {
      alert("Please select a service and at least one of its sub-services.");
      return;
    }
    if (!String(formData.domain || "").trim()) {
      alert("Please select a Business.");
      return;
    }
    if (!formData.businessModel) {
      alert("Please select the Business Model (Project Based or Service Based).");
      return;
    }
    let createdBy = 1;
    try {
      const userStr = getAuthItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        createdBy = user.user_id || user.id || 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }

    const selectedCompanyCountry = countries.find((c) => c.country_name === formData.companyCountry);
    const selectedCompanyState = companyStates.find((s) => s.state_name === formData.companyState);
    const selectedCompanyCity = companyCities.find((c) => c.city_name === formData.companyCity);

    // Source is sent as string (not Source_Id)
    const sourceName = String(formData.source || "").trim() || null;

    // Get Updated_By
    let updatedBy = createdBy;
    try {
      const userStr = getAuthItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        updatedBy = user.user_id || user.id || createdBy;
      }
    } catch (err) {
      console.error("Error reading user for Updated_By:", err);
    }

    const numericServiceIds = formData.services.filter((s) => typeof s === "number");
    const customServiceNames = formData.services
      .filter((s) => typeof s === "string")
      .map((id) => customServices[id]?.name)
      .filter(Boolean);

    const rawDomain = (formData.domain || "").toString().trim();
    let domainName = null;
    let domainId = null;
    if (rawDomain) {
      const match = domains.find((d) => String(d.id) === rawDomain);
      if (match) {
        domainName = (match.name || "").trim() || null;
        domainId = Number(match.id);
      } else if (/^\d+$/.test(rawDomain)) {
        domainId = parseInt(rawDomain, 10);
      } else {
        domainName = rawDomain;
      }
    }

    const payload = {
      Customer_Type: formData.customerType || "Individual",
      First_Name: formatPascal(formData.firstName),
      Last_Name: formData.lastName ? formatPascal(formData.lastName) : null,
      Email: formData.personalEmail || null,
      Country_Code: normalizeCountryCode(personalPhoneCountryCode),
      Phone: toLocalPhone(formData.personalPhone),
      Alt_Phone: toLocalPhone(formData.altPhone),
      Aadhar: (formData.aadhar || "").trim() || null,
      Tax_Number: (formData.taxNumber || "").trim().toUpperCase() || null,
      Company_Email: formData.companyEmail || null,
      Company_Name: formData.companyName ? formatPascal(formData.companyName) : null,
      Company_Phone: toLocalPhone(formData.companyPhone),
      Domain: domainName,
      ...(domainId != null && Number.isFinite(domainId) ? { Domain_Id: domainId } : {}),
      Business_Model: formData.businessModel,
      Storage_Provider: formData.storageProvider || null,
      Source: sourceName,
      Status: formData.status || null,
      Portal_Enabled: 1,
      Portal_Type: formData.portalType,
      Portal_Domain: formData.portalType === PORTAL_OWN_DOMAIN ? portalDomain : null,
      Owner_Company_Email: ownerCompanyEmail,
      ...(!existingOwner && formData.portalPassword ? { Password: formData.portalPassword } : {}),
      ...(formData.isPrimaryCompany && !formData.wasPrimaryCompany ? { Is_Primary_Company: true } : {}),
      Created_By: createdBy,
      Updated_By: updatedBy,
      services: numericServiceIds,
      ...(customServiceNames.length > 0 && { custom_services: customServiceNames }),
      Website: formData.website || null,
      Website_Domain_Name: formData.websiteDomainName || null,
      Com_Lang: (formData.communicationLanguage || "").trim() || null,
      Is_Quote_Sent: formData.isQuoteSent ? 1 : 0,
      Address_Line_1: formData.companyAddressLine1 || null,
      Address_Line_2: formData.companyAddressLine2 || null,
      City_Id: selectedCompanyCity ? selectedCompanyCity.city_id : null,
      State_Id: selectedCompanyState ? selectedCompanyState.state_id : selectedCompanyStateId,
      Country_Id: selectedCompanyCountry ? selectedCompanyCountry.country_id : selectedCompanyCountryId,
      Zip_Code: formData.companyZipCode || null,
      Notes: formData.notes || null,
      Is_Bill_To_Company: !!billToAs,
      ...(billToAs
        ? { Bill_To_As: billToAs }
        : { Default_Bill_To_Id: defaultBillToId ? Number(defaultBillToId) : null })
    };

    setSubmitting(true);
    try {
      try {
        await axiosInstance.put(`/update-client/${clientId}`, payload);
      } catch (firstErr) {
        const status = firstErr?.response?.status;
        const body = firstErr?.response?.data;
        if (status === 404) {
          await axiosInstance.put(`/client/${clientId}`, payload);
        } else if (status === 409 && body?.code === "portal_login_mismatch") {
          const list = (body.mismatched_logins || [])
            .map((m) => `• ${m.Name || "User"} <${m.Company_Email || "no email"}>: ${m.Issue}`)
            .join("\n");
          const ok = await confirm({
            title: "Logins need new emails",
            message: `${body.error}\n\n${list}\n\nSave anyway? These people will appear in Login fixes.`,
          });
          if (!ok) return;
          await axiosInstance.put(`/update-client/${clientId}`, { ...payload, Force_Portal_Change: true });
        } else {
          throw firstErr;
        }
      }
      
      // Create/update followup if followup data is provided
      if (followupData.followupDate) {
        try {
          // Format dates to ISO string format (YYYY-MM-DDTHH:mm:ss)
          const formatDateTime = (dateTimeString) => {
            if (!dateTimeString) return null;
            // datetime-local input returns format: YYYY-MM-DDTHH:mm
            // API expects: YYYY-MM-DDTHH:mm:ss
            return dateTimeString + ":00";
          };

          const followupPayload = {
            Lead_Id: null,
            client_id: clientId,
            Followup_Date: formatDateTime(followupData.followupDate),
            Followup_Type: followupData.followupType || null,
            Remarks: followupData.remarks || null,
            Next_Followup_Date: formatDateTime(followupData.nextFollowupDate),
            Updated_By: updatedBy,
          };

          // If followup exists, update it; otherwise create new
          if (followupId) {
            await axiosInstance.put(`/update-followup/${followupId}`, followupPayload);
          } else {
            followupPayload.Created_By = updatedBy;
            await axiosInstance.post("/create-followup", followupPayload);
          }
        } catch (followupErr) {
          console.error("Error saving followup:", followupErr);
          // Don't fail the whole update if followup creation/update fails
        }
      }
      
      alert("Client updated successfully");
      if (onUpdate) onUpdate();
      if (onBack) onBack();
    } catch (err) {
      console.error("Error updating customer:", err);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to update customer.";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="edittenant-container">
        <div className="edittenant-loading">Loading client details...</div>
      </div>
    );
  }

  const renderCountryCodeSelect = (value, onChange, keyPrefix = "") => (
    <select
      style={{ width: "72px", minWidth: "72px", maxWidth: "72px", flex: "0 0 72px" }}
      value={value || DEFAULT_COUNTRY_CODE}
      onChange={(e) => onChange(normalizeCountryCode(e.target.value || DEFAULT_COUNTRY_CODE))}
      disabled={loadingCountries}
    >
      {countries.map((country) => {
        const code = (country.country_code || country.country_short_code || "").trim();
        if (!code) return null;
        return (
          <option key={`${keyPrefix}${country.country_id}`} value={code}>
            {code}
          </option>
        );
      })}
      {!countries.some((c) => (c.country_code || c.country_short_code || "").trim() === "+91") && (
        <option value="+91">+91</option>
      )}
    </select>
  );

  return (
    <div className="edittenant-container">
      <button type="button" className="edittenant-back-link" onClick={onBack}>
        <FaArrowLeft /> Back to Clients
      </button>
      <div className="issues-main-card">
        <form className="edittenant-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <h3 className="section-title edittenant-subsection-title">Company Details</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>
                Company Name{" "}
                {String(formData.customerType || "").toLowerCase() === "organization" ? (
                  <span className="required">*</span>
                ) : null}
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                required={String(formData.customerType || "").toLowerCase() === "organization"}
              />
            </div>
            <div className="form-group">
              <label>Business <span className="required">*</span></label>
              {domains.length > 0 ? (
                <div className="domain-filter-dropdown" ref={domainDropdownRef} style={{ minWidth: "100%" }}>
                  <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingDomains ? 0.6 : 1, cursor: loadingDomains ? "default" : "pointer" }} onClick={() => !loadingDomains && setDomainDropdownOpen((o) => !o)} role="button" tabIndex={loadingDomains ? -1 : 0} onKeyDown={(e) => { if (!loadingDomains && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setDomainDropdownOpen((o) => !o); } }}>
                    <span>{loadingDomains ? "Loading..." : (formData.domain ? (domains.find((d) => String(d.id) === String(formData.domain))?.name || "Select Business") : "Select Business")}</span>
                    <FaChevronDown className={`domain-filter-chevron ${domainDropdownOpen ? "open" : ""}`} />
                  </div>
                  {domainDropdownOpen && !loadingDomains && (
                    <div className="domain-filter-panel">
                      <input type="text" className="domain-filter-search" placeholder="Search..." value={domainSearchQuery} onChange={(e) => setDomainSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                      <div className="domain-filter-options">
                        <div className={`domain-filter-option ${!formData.domain ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, domain: "" })); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>Select Business</div>
                        {domains.filter((d) => !domainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase())).map((d) => (
                          <div key={d.id} className={`domain-filter-option ${String(formData.domain) === String(d.id) ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, domain: String(d.id), businessModel: p.businessModel || d.defaultModel })); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>{d.name}</div>
                        ))}
                        {domains.filter((d) => !domainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No domains found</div>}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  name="domain"
                  value={formData.domain}
                  onChange={handleInputChange}
                  required
                />
              )}
            </div>
            <div className="form-group">
              <label>Business Model <span className="required">*</span></label>
              <select
                name="businessModel"
                value={formData.businessModel}
                onChange={handleInputChange}
                required
              >
                <option value="">Select model</option>
                {BUSINESS_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Company Email</label>
              <input
                type="email"
                name="companyEmail"
                value={formData.companyEmail}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Company Phone</label>
              <div className="edittenant-phone-row">
                {renderCountryCodeSelect(companyPhoneCountryCode, setCompanyPhoneCountryCode, "company-")}
                <input
                  type="text"
                  name="companyPhone"
                  value={formData.companyPhone}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Website</label>
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://www.example.com"
              />
            </div>
            <div className="form-group">
              <label>Domain Name</label>
              <input
                type="text"
                name="websiteDomainName"
                value={formData.websiteDomainName}
                onChange={handleInputChange}
                placeholder="example.com"
              />
            </div>
            {formData.ownerBusinessCount > 1 ? (
              <div className="form-group">
                <label>Primary Company</label>
                <label
                  className={`primary-switch ${formData.isPrimaryCompany ? "is-on" : ""} ${
                    formData.wasPrimaryCompany ? "is-locked" : ""
                  }`}
                  title={
                    formData.wasPrimaryCompany
                      ? "To change it, make another company of this owner primary."
                      : undefined
                  }
                >
                  <input
                    type="checkbox"
                    checked={!!formData.isPrimaryCompany}
                    disabled={formData.wasPrimaryCompany}
                    onChange={(e) => setFormData((prev) => ({ ...prev, isPrimaryCompany: e.target.checked }))}
                  />
                  <span className="primary-switch__track" aria-hidden="true">
                    <span className="primary-switch__thumb" />
                  </span>
                  <span className="primary-switch__text">
                    {formData.isPrimaryCompany ? "This company" : "Not primary"}
                  </span>
                </label>
              </div>
            ) : null}
            <BillToSelfSwitch
              kind="Company"
              value={billToAs}
              onChange={setBillToAs}
              disabled={!formData.companyName.trim()}
              disabledReason="Enter a company name to bill this client as a company."
            />
          </div>
        </div>

        <div className="form-section edittenant-section-divider">
          <h3 className="section-title edittenant-subsection-title">
            Company Address
            <InfoTip text="Office or business location for this client." label="About Company Address" />
          </h3>
          <div className="form-grid">
            <AddressSearch
              onSelect={addressAutofill.onSelect}
              viewParts={[
                formData.companyAddressLine1,
                formData.companyAddressLine2,
                formData.companyCity,
                formData.companyState,
                formData.companyZipCode,
                formData.companyCountry,
              ]}
            />
            <div className="form-group" style={{ gridColumnStart: 1 }}>
              <label>Address Line 1</label>
              <input
                type="text"
                name="companyAddressLine1"
                value={formData.companyAddressLine1}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Address Line 2</label>
              <input
                type="text"
                name="companyAddressLine2"
                value={formData.companyAddressLine2}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Country</label>
              <select
                name="companyCountry"
                value={formData.companyCountry}
                onChange={handleInputChange}
                disabled={loadingCountries}
              >
                <option value="">{formData.companyCountry || "Select Country"}</option>
                {countries.map((country) => (
                  <option key={`company-${country.country_id}`} value={country.country_name}>
                    {country.country_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>State</label>
              <select
                name="companyState"
                value={formData.companyState}
                onChange={handleInputChange}
                disabled={!selectedCompanyCountryId}
              >
                <option value="">{formData.companyState || "Select State"}</option>
                {companyStates.map((state) => (
                  <option key={`company-${state.state_id}`} value={state.state_name}>
                    {state.state_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>City</label>
              <select
                name="companyCity"
                value={formData.companyCity}
                onChange={handleInputChange}
                disabled={!selectedCompanyStateId}
              >
                <option value="">{formData.companyCity || "Select City"}</option>
                {companyCities.map((city) => (
                  <option key={`company-${city.city_id}`} value={city.city_name}>
                    {city.city_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Zip Code</label>
              <input
                type="text"
                name="companyZipCode"
                value={formData.companyZipCode}
                onChange={handleInputChange}
              />
            </div>
            {addressAutofill.note ? (
              <div className="form-group address-map-row">
                <small className="form-hint">{addressAutofill.note}</small>
              </div>
            ) : null}
          </div>
        </div>

        <div className="form-section edittenant-section-divider">
          <h3 className="section-title edittenant-subsection-title">Personal Details</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Client ID</label>
              <input type="text" value={clientId || ""} disabled readOnly />
            </div>
            <div className="form-group">
              <label>First Name <span className="required">*</span></label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name <span className="required">*</span></label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Personal Email <span className="required">*</span></label>
              <input
                type="email"
                name="personalEmail"
                value={formData.personalEmail}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Personal Phone <span className="required">*</span></label>
              <div className="edittenant-phone-row">
                {renderCountryCodeSelect(personalPhoneCountryCode, setPersonalPhoneCountryCode)}
                <input
                  type="text"
                  name="personalPhone"
                  value={formData.personalPhone}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Alternate phone</label>
              <div className="edittenant-phone-row">
                {renderCountryCodeSelect(altPhoneCountryCode, setAltPhoneCountryCode, "alt-")}
                <input
                  type="text"
                  name="altPhone"
                  value={formData.altPhone}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Aadhar / SSN</label>
              <input
                type="text"
                name="aadhar"
                value={formData.aadhar}
                onChange={handleInputChange}
                placeholder="Aadhar or SSN"
                maxLength={14}
              />
            </div>
            <div className="form-group">
              <label>PAN / EIN</label>
              <input
                type="text"
                name="taxNumber"
                value={formData.taxNumber}
                onChange={handleInputChange}
                placeholder="PAN, EIN or TIN"
                maxLength={20}
                style={{ textTransform: "uppercase" }}
              />
            </div>
            <BillToSelfSwitch kind="Individual" value={billToAs} onChange={setBillToAs} />
          </div>
        </div>

        <PortalLoginFields
          classPrefix="edittenant"
          portalType={formData.portalType}
          portalDomain={formData.portalDomain}
          suggestedDomain={formData.websiteDomainName || formData.website}
          ownerEmail={formData.ownerCompanyEmail}
          onChange={({ portalType, portalDomain }) =>
            setFormData((prev) => ({
              ...prev,
              portalType,
              portalDomain,
              ownerCompanyEmail: autoOwnerEmail(prev.ownerCompanyEmail, portalType, portalDomain),
            }))
          }
        />

        <div className="form-section edittenant-section-divider">
          <h3 className="section-title edittenant-subsection-title">Account</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>
                Owner Login Email <span className="required">*</span>
                <InfoTip
                  label="About Owner Login Email"
                  text={`${OWNER_EMAIL_INFO} ${ownerEmailHint(formData.portalType, formData.portalDomain)}`}
                />
              </label>
              <input
                type="email"
                name="ownerCompanyEmail"
                value={formData.ownerCompanyEmail}
                onChange={handleInputChange}
                required
                autoComplete="off"
                placeholder={ownerEmailPlaceholder(formData.portalType, formData.portalDomain)}
              />
              {ownerDomainError(formData.ownerCompanyEmail, formData.portalType, formData.portalDomain) ? (
                <small className="form-hint" style={{ color: "#dc3545" }}>
                  {ownerDomainError(formData.ownerCompanyEmail, formData.portalType, formData.portalDomain)}
                </small>
              ) : existingOwner ? (
                <small className="form-hint">{describeExistingOwner(existingOwner)}</small>
              ) : null}
            </div>
            {!existingOwner && (
              <OwnerPasswordField
                label={formData.hasPassword ? "Reset Portal Password" : "Password"}
                required={!formData.hasPassword}
                placeholder={formData.hasPassword ? "Leave blank to keep unchanged" : undefined}
                value={formData.portalPassword}
                onChange={(portalPassword) => setFormData((prev) => ({ ...prev, portalPassword }))}
                loginEmail={String(formData.ownerCompanyEmail || "").trim()}
                portalUrl={`https://${portalHostFor(formData.portalType, formData.portalDomain)}`}
                recipientName={String(formData.firstName || "").trim()}
                recipientEmail={String(formData.personalEmail || "").trim()}
                recipientPhone={`${personalPhoneCountryCode}${formData.personalPhone || ""}`}
              />
            )}
            <div className="form-group" style={{ gridColumnStart: 1 }}>
              <label>Client Type</label>
              <select
                name="customerType"
                value={formData.customerType}
                onChange={handleInputChange}
              >
                {CUSTOMER_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                className="tenant-narrow-control"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="">Select status</option>
                {CUSTOMER_STATUSES.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Quote</label>
              <div className="tenant-quote-row">
                {formData.isQuoteSent ? (
                  <>
                    <span className="tenant-quote-sent">Sent</span>
                    <button type="button" className="tenant-quote-btn" onClick={viewLatestQuote}>
                      View Quote
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="tenant-quote-btn"
                    onClick={() =>
                      navigate("/generate-quote", {
                        state: { customerData: formData, type: "client", clientId }
                      })
                    }
                  >
                    Generate Quote
                  </button>
                )}
              </div>
            </div>
            <div className="form-group" style={{ gridColumnStart: 1 }}>
              <label>Communication Language</label>
              <input
                type="text"
                name="communicationLanguage"
                value={formData.communicationLanguage}
                onChange={handleInputChange}
              />
            </div>
            <TenantSourceField
              value={formData.source}
              onChange={(source) => setFormData((prev) => ({ ...prev, source }))}
              controlClassName="tenant-narrow-control"
            />
            <StorageProviderField
              value={formData.storageProvider}
              savedValue={formData.originalStorageProvider}
              onChange={(storageProvider) => setFormData((prev) => ({ ...prev, storageProvider }))}
            />
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                style={{ resize: "vertical", minHeight: "60px" }}
              />
            </div>
          </div>
        </div>

        <TenantBillToSection
          clientId={clientId}
          billToAs={billToAs}
          companyName={formData.companyName}
          personName={`${formData.firstName || ""} ${formData.lastName || ""}`}
          billToId={defaultBillToId}
          onBillToChange={setDefaultBillToId}
        />

        <div className="form-section edittenant-section-divider">
          <h2 className="section-title">Services <span className="required">*</span></h2>
          {loadingServices ? (
            <p>Loading services...</p>
          ) : (
            <ServicePicker
              categories={serviceCategories}
              value={formData.services}
              onChange={(services) => setFormData((prev) => ({ ...prev, services }))}
              customServices={customServices}
              onDeleteCustom={handleDeleteCustomService}
            />
          )}
        </div>

        <div className="form-section edittenant-section-divider">
          <h2 className="section-title">Latest Followup</h2>
          <div className="edittenant-followup-grid">
            <div className="form-group">
              <label>Followup Date</label>
              <input
                type="datetime-local"
                name="followupDate"
                value={followupData.followupDate}
                onChange={handleFollowupChange}
              />
            </div>
            <div className="form-group">
              <label>Followup Type</label>
              <input
                type="text"
                name="followupType"
                value={followupData.followupType}
                onChange={handleFollowupChange}
              />
            </div>
            <div className="form-group">
              <label>Next Followup Date</label>
              <input
                type="datetime-local"
                name="nextFollowupDate"
                value={followupData.nextFollowupDate}
                onChange={handleFollowupChange}
              />
            </div>
          </div>
          <div className="edittenant-remarks-group">
            <label>Remarks</label>
            <textarea
              name="remarks"
              value={followupData.remarks}
              onChange={handleFollowupChange}
              rows={5}
            
            />
          </div>
        </div>

        <div className="edittenant-actions">
          <button type="submit" className="btn-update-tenant" disabled={submitting}>
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
      </div>
      {editingService && (
        <div className="addlead-modal-overlay" onClick={handleCancelEditService}>
          <div className="addlead-modal addlead-add-service-modal" onClick={e => e.stopPropagation()}>
            <h3>{isAddingServiceToCategory ? `Add Service to ${serviceCategories.find(c => c.category_id === isAddingServiceToCategory)?.category_name || "Category"}` : "Edit Service"}</h3>
            <div className="form-group">
              <label>Service Name</label>
              <input
                type="text"
                value={editingService.name}
                onChange={e => setEditingService({ ...editingService, name: e.target.value })}
            
              />
            </div>
            <div className="addlead-modal-actions">
              <button type="button" className="btn-cancel" onClick={handleCancelEditService}>Cancel</button>
              <button type="button" className="btn-update-tenant" onClick={handleSaveService} disabled={!editingService.name?.trim()}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditTenant;
